// src/services/cheques/chequeService.js
import { createCrudService } from '../api/baseService';
import easyCheckApi, { easyCheckRequest } from '../api/config';

// Create base CRUD operations for cheques
const chequeBaseCrud = createCrudService('cheques');

// Adobe PDF Reader Detection and Opening Utilities
const detectAdobePDF = () => {
  try {
    // Check for Adobe Acrobat/Reader plugin
    const plugins = navigator.plugins;
    for (let i = 0; i < plugins.length; i++) {
      const plugin = plugins[i];
      if (plugin.name.toLowerCase().includes('adobe') && 
          plugin.name.toLowerCase().includes('pdf')) {
        return true;
      }
    }
    
    // Check for Adobe PDF MIME type support
    const mimeTypes = navigator.mimeTypes;
    const adobeMimeType = mimeTypes['application/pdf'];
    if (adobeMimeType && adobeMimeType.enabledPlugin && 
        adobeMimeType.enabledPlugin.name.toLowerCase().includes('adobe')) {
      return true;
    }
    
    return false;
  } catch (error) {
    console.warn('Could not detect Adobe PDF Reader:', error);
    return false;
  }
};

const forceAdobePDFDownload = (blob, filename) => {
  // Force download to open with system's default PDF viewer (hopefully Adobe)
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  // Add special attributes to indicate Adobe PDF requirement
  link.setAttribute('data-adobe-pdf-required', 'true');
  link.setAttribute('title', 'Download to open with Adobe PDF Reader for best printing experience');
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up after download
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 1000);
  
  return true;
};

const openWithAdobePDFInstructions = (blob, filename) => {
  // Create a more explicit instruction page
  const url = window.URL.createObjectURL(blob);
  const newWindow = window.open('', '_blank', 
    'width=1000,height=700,scrollbars=yes,resizable=yes,toolbar=yes,menubar=yes'
  );
  
  if (newWindow) {
    newWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Adobe PDF Reader Required - ${filename}</title>
        <style>
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            margin: 0; 
            padding: 20px; 
            background: #f8f9fa;
            line-height: 1.6;
          }
          .container { 
            max-width: 800px; 
            margin: 0 auto; 
            background: white; 
            padding: 30px; 
            border-radius: 8px; 
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          .header { 
            background: linear-gradient(135deg, #dc3545, #e74c3c);
            color: white; 
            padding: 20px; 
            border-radius: 8px 8px 0 0; 
            margin: -30px -30px 30px -30px;
            text-align: center;
          }
          .warning { 
            background: #fff3cd; 
            color: #856404; 
            padding: 15px; 
            border-left: 4px solid #ffc107; 
            margin: 20px 0;
            border-radius: 4px;
          }
          .success { 
            background: #d4edda; 
            color: #155724; 
            padding: 15px; 
            border-left: 4px solid #28a745; 
            margin: 20px 0;
            border-radius: 4px;
          }
          .download-btn { 
            display: inline-block;
            background: #007bff; 
            color: white; 
            padding: 12px 24px; 
            text-decoration: none; 
            border-radius: 5px;
            font-weight: bold;
            margin: 10px 5px;
            transition: background 0.3s;
          }
          .download-btn:hover { 
            background: #0056b3; 
          }
          .adobe-btn {
            background: #dc143c;
          }
          .adobe-btn:hover {
            background: #b91c2b;
          }
          .steps { 
            background: #e9ecef; 
            padding: 20px; 
            border-radius: 5px; 
            margin: 20px 0;
          }
          .step { 
            margin: 15px 0; 
            padding: 10px 0;
            border-bottom: 1px solid #dee2e6;
          }
          .step:last-child { 
            border-bottom: none; 
          }
          .step-number { 
            display: inline-block;
            background: #007bff; 
            color: white; 
            width: 25px; 
            height: 25px; 
            border-radius: 50%; 
            text-align: center; 
            line-height: 25px; 
            margin-right: 10px;
            font-weight: bold;
          }
          .print-settings {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            padding: 15px;
            border-radius: 5px;
            margin: 15px 0;
          }
          .highlight {
            background: yellow;
            padding: 2px 4px;
            border-radius: 2px;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>🚨 Adobe PDF Reader Required for Cheque Printing</h1>
            <p>Custom paper sizes require Adobe PDF Reader for accurate printing</p>
          </div>
          
          <div class="warning">
            <strong>⚠️ Important:</strong> Your browser's built-in PDF viewer doesn't support the custom paper sizes used for cheques. 
            Adobe PDF Reader is required for proper scaling and paper size selection.
          </div>

          <div class="steps">
            <h3>📋 How to Print Your Cheque Correctly:</h3>
            
            <div class="step">
              <span class="step-number">1</span>
              <strong>Download Adobe PDF Reader (if not installed):</strong><br>
              <a href="https://get.adobe.com/reader/" target="_blank" class="download-btn adobe-btn">
                📥 Download Adobe PDF Reader (Free)
              </a>
            </div>
            
            <div class="step">
              <span class="step-number">2</span>
              <strong>Download the cheque PDF:</strong><br>
              <a href="${url}" download="${filename}" class="download-btn">
                📄 Download ${filename}
              </a>
            </div>
            
            <div class="step">
              <span class="step-number">3</span>
              <strong>Open the downloaded PDF with Adobe PDF Reader</strong><br>
              Right-click the downloaded file → "Open with" → "Adobe Acrobat Reader"
            </div>
            
            <div class="step">
              <span class="step-number">4</span>
              <strong>Print with correct settings:</strong>
              <div class="print-settings">
                <p><strong>In Adobe PDF Reader's Print Dialog:</strong></p>
                <ul>
                  <li>Set Scale to: <span class="highlight">"Actual Size" or "100%"</span></li>
                  <li>Choose Paper Size: <span class="highlight">Match your cheque paper size</span></li>
                  <li>Orientation: <span class="highlight">Portrait</span></li>
                  <li>Quality: <span class="highlight">High (600 DPI or higher)</span></li>
                  <li>⚠️ Make sure <span class="highlight">"Fit to Page" is DISABLED</span></li>
                </ul>
              </div>
            </div>
          </div>

          <div class="success">
            <strong>✅ Why This Matters:</strong><br>
            Adobe PDF Reader ensures your cheque prints at exactly the right size with proper alignment. 
            Browser PDF viewers often resize content, which can cause misalignment on your cheque paper.
          </div>

          <p style="text-align: center; color: #6c757d; margin-top: 30px;">
            <strong>EasyCheck</strong> - Professional Cheque Management System
          </p>
        </div>
      </body>
      </html>
    `);
    newWindow.document.close();
    
    // Also trigger the download
    forceAdobePDFDownload(blob, filename);
    
    return true;
  }
  
  return false;
};

export const chequeService = {
  ...chequeBaseCrud,

  // Get next cheque number suggestion for a bank
  getNextChequeNumber: async (bankId) => {
    try {
      const response = await easyCheckApi.get(`/cheques/next-number/${bankId}`);
      const responseData = response.data;
      if (responseData && (responseData.Success !== false)) {
        const data = responseData.Data || responseData.data || responseData;
        return {
          success: true,
          data: {
            nextChequeNumber: data.NextChequeNumber || data.nextChequeNumber || '',
            hasNumericHistory: data.HasNumericHistory ?? data.hasNumericHistory ?? false,
            lastChequeNumber: data.LastChequeNumber || data.lastChequeNumber || ''
          }
        };
      }
      return { success: false, message: responseData?.Message || 'Failed to fetch next cheque number' };
    } catch (error) {
      console.error('Error fetching next cheque number:', error);
      return handleApiError(error);
    }
  },

  // Get all cheques using the correct endpoint
  getPrintedCheques: async (params = {}) => {
    try {
      const response = await easyCheckApi.get('/cheques', {
        params: {
          pageNumber: 1,
          pageSize: 10,
          status: 'Pending', // Default to pending cheques
          ...params
        }
      });
      
      console.log('📥 Get Cheques API Response:', response.data);
      
      // Handle the API response format consistently
      const responseData = response.data;
      if (responseData && (responseData.Success !== false)) {
        return {
          success: true,
          data: responseData.Data || responseData.data || responseData,
          totalCount: responseData.TotalCount || responseData.totalCount || 0,
          totalPages: responseData.TotalPages || responseData.totalPages || 1,
          pageNumber: responseData.PageNumber || responseData.pageNumber || 1,
          pageSize: responseData.PageSize || responseData.pageSize || 10,
          hasNextPage: responseData.HasNextPage || responseData.hasNextPage || false,
          hasPreviousPage: responseData.HasPreviousPage || responseData.hasPreviousPage || false
        };
      } else {
        return {
          success: false,
          message: responseData.Message || responseData.message || 'Failed to fetch cheques'
        };
      }
    } catch (error) {
      console.error('❌ Get Cheques API Error:', error);
      return handleApiError(error);
    }
  },

  // Create new cheque
  createPrintedCheque: async (chequeData) => {
    try {
      // Include chequeNumber in payload if provided (non-empty)
      const payload = { ...chequeData };
      if (!payload.chequeNumber) {
        delete payload.chequeNumber;
      }
      console.log('📤 Sending cheque data to API:', payload);
      const response = await easyCheckApi.post('/cheques', payload);
      console.log('📥 API Response:', response.data);
      
      // Handle the API response format as shown in the example
      if (response.data && (response.data.Success || response.data.success)) {
        return {
          success: true,
          data: response.data.Data || response.data.data || response.data,
          message: response.data.Message || response.data.message || 'Success'
        };
      } else {
        return {
          success: false,
          message: response.data.Message || response.data.message || 'Unknown error'
        };
      }
    } catch (error) {
      console.error('❌ API Error:', error);
      return handleApiError(error);
    }
  },

  // Create multiple cheques (for recurring PDC)
  createMultipleCheques: async (chequesData) => {
    try {
      console.log('📤 Creating multiple cheques:', chequesData);
      const promises = chequesData.map(cheque => 
        easyCheckApi.post('/cheques', cheque)
      );
      const responses = await Promise.all(promises);
      console.log('📥 Multiple cheques responses:', responses);
      
      // Handle the responses according to the API format
      const successfulResponses = responses.filter(response => 
        response.data && (response.data.Success || response.data.success !== false)
      );
      
      if (successfulResponses.length === responses.length) {
        return {
          success: true,
          data: responses.map(response => response.data.Data || response.data.data || response.data),
          message: `${responses.length} cheques created successfully`
        };
      } else {
        return {
          success: false,
          message: `Only ${successfulResponses.length} out of ${responses.length} cheques were created`
        };
      }
    } catch (error) {
      console.error('❌ Multiple Cheques API Error:', error);
      return handleApiError(error);
    }
  },

  // Update existing cheque
  updatePrintedCheque: async (id, chequeData) => {
    try {
      console.log('✏️ Updating cheque ID:', id, 'with data:', chequeData);
      const response = await easyCheckApi.put(`/cheques/${id}`, chequeData);
      console.log('✏️ Update response:', response.data);
      
      // Handle the API response format
      const responseData = response.data;
      if (responseData && (responseData.Success !== false)) {
        return {
          success: true,
          data: responseData.Data || responseData.data || responseData,
          message: responseData.Message || responseData.message || 'Cheque updated successfully'
        };
      } else {
        return {
          success: false,
          message: responseData.Message || responseData.message || 'Failed to update cheque'
        };
      }
    } catch (error) {
      console.error('❌ Update Cheque API Error:', error);
      return handleApiError(error);
    }
  },

  // Delete cheque
  deletePrintedCheque: async (id) => {
    try {
      console.log('🗑️ Deleting cheque ID:', id);
      const response = await easyCheckApi.delete(`/cheques/${id}`);
      console.log('🗑️ Delete response:', response.data);
      
      // Handle the API response format
      const responseData = response.data;
      if (responseData && (responseData.Success !== false)) {
        return {
          success: true,
          data: responseData.Data || responseData.data || responseData,
          message: responseData.Message || responseData.message || 'Cheque deleted successfully'
        };
      } else {
        return {
          success: false,
          message: responseData.Message || responseData.message || 'Failed to delete cheque'
        };
      }
    } catch (error) {
      console.error('❌ Delete Cheque API Error:', error);
      return handleApiError(error);
    }
  },

  // Get deleted cheques
  getDeletedCheques: async (params = {}) => {
    try {
      const response = await easyCheckApi.get('/cheques/deleted', {
        params: {
          pageNumber: params.pageNumber || 1,
          pageSize: params.pageSize || 10000,
          bankId: params.bankId || undefined,
          status: params.status || undefined,
          startDate: params.startDate || undefined,
          endDate: params.endDate || undefined,
          search: params.search || undefined,
        }
      });
      const responseData = response.data;
      if (responseData && responseData.Success !== false) {
        return {
          success: true,
          data: responseData.Data || responseData.data || [],
          totalCount: responseData.TotalCount || responseData.totalCount || 0,
          message: responseData.Message || responseData.message || 'Deleted cheques retrieved'
        };
      } else {
        return {
          success: false,
          data: [],
          message: responseData.Message || responseData.message || 'Failed to retrieve deleted cheques'
        };
      }
    } catch (error) {
      console.error('❌ Get Deleted Cheques API Error:', error);
      return handleApiError(error);
    }
  },

  // Get dashboard statistics (NEW ENDPOINT)
  getDashboardStatistics: async (params = {}) => {
    try {
      console.log('📊 Fetching dashboard statistics...');
      const response = await easyCheckApi.get('/cheques/dashboard-stats', { params });
      console.log('✅ Dashboard stats response:', response.data);
      console.log('✅ Dashboard stats Data field:', response.data?.Data);

      // Extract the actual data from the API response structure
      const apiData = response.data?.Data || response.data?.data || response.data;
      console.log('✅ Extracted API data:', apiData);

      return {
        success: true,
        data: apiData
      };
    } catch (error) {
      console.error('❌ Dashboard Stats Error:', error);
      return handleApiError(error);
    }
  },

  // Get upcoming payments (NEW ENDPOINT)
  getUpcomingPayments: async (params = {}) => {
    try {
      console.log('📅 Fetching upcoming payments with params:', params);
      const response = await easyCheckApi.get('/cheques/upcoming', { params });
      console.log('✅ Upcoming payments response:', response.data);
      console.log('✅ Upcoming payments Data field:', response.data?.Data);
      
      const responseData = response.data;
      return {
        success: true,
        data: responseData?.Data || responseData?.data || responseData || [],
        totalCount: responseData?.TotalCount || responseData?.totalCount || 0,
        pageNumber: responseData?.PageNumber || responseData?.pageNumber || 1,
        pageSize: responseData?.PageSize || responseData?.pageSize || 50,
        totalPages: responseData?.TotalPages || responseData?.totalPages || 1,
        hasNextPage: responseData?.HasNextPage || responseData?.hasNextPage || false,
        hasPreviousPage: responseData?.HasPreviousPage || responseData?.hasPreviousPage || false
      };
    } catch (error) {
      console.error('❌ Upcoming Payments Error:', error);
      return handleApiError(error);
    }
  },

  // Get bank-wise cheque summary
  getBankSummary: async () => {
    try {
      const response = await easyCheckApi.get('/cheques/bank-summary');
      const apiData = response.data?.Data || response.data?.data || response.data;
      return { success: true, data: apiData };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Legacy statistics endpoint (DEPRECATED - kept for backward compatibility)
  getChequeStatistics: async () => {
    console.warn('⚠️ getChequeStatistics is deprecated. Use getDashboardStatistics instead.');
    return await chequeService.getDashboardStatistics();
  },

  // Existing cheque-specific endpoints
  getChequesByAccount: async (accountId, params = {}) => {
    return easyCheckRequest(() => 
      easyCheckApi.get(`/cheques/account/${accountId}`, { params })
    );
  },

  printCheque: async (chequeData) => {
    return easyCheckRequest(() => 
      easyCheckApi.post('/cheques/print', chequeData)
    );
  },

  printBulkCheques: async (chequeIds, printOptions = {}) => {
    try {
      console.log('🖨️ Printing bulk cheques:', chequeIds, 'with options:', printOptions);
      
      const requestData = { 
        ChequeIds: chequeIds,
        // Include print options in the request body
        PrintOptions: {
          IncludeAcPayee: printOptions.includeAcPayee !== undefined ? printOptions.includeAcPayee : true,
          IncludeBeneficiary: printOptions.includeBeneficiary !== undefined ? printOptions.includeBeneficiary : true,
          IncludeDate: printOptions.includeDate !== undefined ? printOptions.includeDate : true,
          IncludeAmount: printOptions.includeAmount !== undefined ? printOptions.includeAmount : true,
          IncludeAmountPrefix: printOptions.includeAmountPrefix !== undefined ? printOptions.includeAmountPrefix : false,
          IncludeAmountSuffix: printOptions.includeAmountSuffix !== undefined ? printOptions.includeAmountSuffix : false,
          IncludeNonNegotiable: printOptions.includeNonNegotiable !== undefined ? printOptions.includeNonNegotiable : false,
          IncludeBeneficiary2: printOptions.includeBeneficiary2 !== undefined ? printOptions.includeBeneficiary2 : false,
          IncludeRemarks: printOptions.includeRemarks !== undefined ? printOptions.includeRemarks : false,
          ChequeSizedPdf: printOptions.chequeSizedPdf !== undefined ? printOptions.chequeSizedPdf : false,
          ExcludeCurrencyWords: printOptions.excludeCurrencyWords !== undefined ? printOptions.excludeCurrencyWords : false
        }
      };
      
      const response = await easyCheckApi.post('/cheques/pdf/bulk-print-pdf', requestData, {
        responseType: 'blob'
      });
      
      // Create blob and open directly in Chrome
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Open PDF directly in new tab/window using Chrome's built-in PDF viewer
      const newWindow = window.open(blobUrl, '_blank', 'width=1000,height=700,scrollbars=yes,resizable=yes');
      
      if (newWindow) {
        // Clean up the object URL after a delay to ensure the PDF loads
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 5000);
      } else {
        // If popup blocked, create a download link as fallback
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `cheques-bulk-${Date.now()}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
      
      return {
        success: true,
        message: `${chequeIds.length} cheques PDF opened in new tab`,
        requiresAdobePDF: false
      };
    } catch (error) {
      console.error('❌ Bulk Print Cheques API Error:', error);
      return handleApiError(error);
    }
  },

  getChequePreview: async (chequeId) => {
    return easyCheckRequest(() => 
      easyCheckApi.get(`/cheques/${chequeId}/preview`)
    );
  },

  validateChequeNumber: async (accountId, chequeNumber) => {
    return easyCheckRequest(() => 
      easyCheckApi.post('/cheques/validate-number', { accountId, chequeNumber })
    );
  },

  voidCheque: async (chequeId, reason) => {
    try {
      const response = await easyCheckApi.post(`/cheques/${chequeId}/cancel`, { reason });
      const responseData = response.data;
      if (responseData && (responseData.Success !== false)) {
        return {
          success: true,
          data: responseData.Data || responseData.data || responseData,
          message: responseData.Message || responseData.message || 'Cheque cancelled successfully'
        };
      } else {
        return {
          success: false,
          message: responseData.Message || responseData.message || 'Failed to cancel cheque'
        };
      }
    } catch (error) {
      console.error('Cancel Cheque API Error:', error);
      return handleApiError(error);
    }
  },

  // Mark cheques as cleared
  markCleared: async (chequeIds, clearedDate) => {
    try {
      const response = await easyCheckApi.post('/cheques/mark-cleared', { chequeIds, clearedDate });
      const responseData = response.data;
      if (responseData && (responseData.Success !== false)) {
        return {
          success: true,
          data: responseData.Data || responseData.data || responseData,
          message: responseData.Message || responseData.message || 'Cheques marked as cleared'
        };
      } else {
        return {
          success: false,
          message: responseData.Message || responseData.message || 'Failed to mark cheques as cleared'
        };
      }
    } catch (error) {
      console.error('Mark Cleared API Error:', error);
      return handleApiError(error);
    }
  },

  // Mark cheques as uncleared (reverse clearing)
  markUncleared: async (chequeIds) => {
    try {
      const response = await easyCheckApi.post('/cheques/mark-uncleared', { chequeIds });
      const responseData = response.data;
      if (responseData && (responseData.Success !== false)) {
        return {
          success: true,
          data: responseData.Data || responseData.data || responseData,
          message: responseData.Message || responseData.message || 'Cheques marked as uncleared'
        };
      } else {
        return {
          success: false,
          message: responseData.Message || responseData.message || 'Failed to mark cheques as uncleared'
        };
      }
    } catch (error) {
      console.error('Mark Uncleared API Error:', error);
      return handleApiError(error);
    }
  },

  // Mark cheques as signed (Printed -> Signed) — optional Signed Status module
  markSigned: async (chequeIds) => {
    try {
      const response = await easyCheckApi.post('/cheques/mark-signed', { chequeIds });
      const responseData = response.data;
      if (responseData && (responseData.Success !== false)) {
        return {
          success: true,
          data: responseData.Data || responseData.data || responseData,
          message: responseData.Message || responseData.message || 'Cheques marked as signed'
        };
      } else {
        return {
          success: false,
          message: responseData.Message || responseData.message || 'Failed to mark cheques as signed'
        };
      }
    } catch (error) {
      console.error('Mark Signed API Error:', error);
      return handleApiError(error);
    }
  },

  // Revert signed cheques back to printed (Signed -> Printed)
  markUnsigned: async (chequeIds) => {
    try {
      const response = await easyCheckApi.post('/cheques/mark-unsigned', { chequeIds });
      const responseData = response.data;
      if (responseData && (responseData.Success !== false)) {
        return {
          success: true,
          data: responseData.Data || responseData.data || responseData,
          message: responseData.Message || responseData.message || 'Cheques reverted to printed'
        };
      } else {
        return {
          success: false,
          message: responseData.Message || responseData.message || 'Failed to revert signed cheques'
        };
      }
    } catch (error) {
      console.error('Mark Unsigned API Error:', error);
      return handleApiError(error);
    }
  },

  getChequeHistory: async (chequeId) => {
    return easyCheckRequest(() => 
      easyCheckApi.get(`/cheques/${chequeId}/history`)
    );
  },

  // Print single cheque and get PDF with options
  printSingleCheque: async (chequeId, printOptions = {}) => {
    try {
      console.log('🖨️ Printing single cheque ID:', chequeId, 'with options:', printOptions);
      
      // Prepare query parameters for print options
      const params = new URLSearchParams();
      if (printOptions.includeAcPayee !== undefined) {
        params.append('includeAcPayee', printOptions.includeAcPayee);
      }
      if (printOptions.includeBeneficiary !== undefined) {
        params.append('includeBeneficiary', printOptions.includeBeneficiary);
      }
      if (printOptions.includeDate !== undefined) {
        params.append('includeDate', printOptions.includeDate);
      }
      if (printOptions.includeAmount !== undefined) {
        params.append('includeAmount', printOptions.includeAmount);
      }
      if (printOptions.includeNonNegotiable !== undefined) {
        params.append('includeNonNegotiable', printOptions.includeNonNegotiable);
      }
      if (printOptions.includeAmountPrefix !== undefined) {
        params.append('includeAmountPrefix', printOptions.includeAmountPrefix);
      }
      if (printOptions.includeAmountSuffix !== undefined) {
        params.append('includeAmountSuffix', printOptions.includeAmountSuffix);
      }
      if (printOptions.includeBeneficiary2 !== undefined) {
        params.append('includeBeneficiary2', printOptions.includeBeneficiary2);
      }
      if (printOptions.includeRemarks !== undefined) {
        params.append('includeRemarks', printOptions.includeRemarks);
      }
      if (printOptions.chequeSizedPdf !== undefined) {
        params.append('chequeSizedPdf', printOptions.chequeSizedPdf);
      }
      if (printOptions.excludeCurrencyWords !== undefined) {
        params.append('excludeCurrencyWords', printOptions.excludeCurrencyWords);
      }

      const queryString = params.toString();
      const url = `/cheques/pdf/${chequeId}/GetChqPdf${queryString ? `?${queryString}` : ''}`;
      
      const response = await easyCheckApi.get(url, {
        responseType: 'blob' // For PDF viewing
      });
      
      // Create blob and open directly in Chrome
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Open PDF directly in new tab/window using Chrome's built-in PDF viewer
      const newWindow = window.open(blobUrl, '_blank', 'width=1000,height=700,scrollbars=yes,resizable=yes');
      
      if (newWindow) {
        // Clean up the object URL after a delay to ensure the PDF loads
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 5000);
      } else {
        // If popup blocked, create a download link as fallback
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `cheque-${chequeId}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
      
      return {
        success: true,
        message: 'Cheque PDF opened in new tab',
        requiresAdobePDF: false
      };
    } catch (error) {
      console.error('❌ Print Single Cheque API Error:', error);
      return handleApiError(error);
    }
  },

  // Get cheque PDF using your specific GetChqPdf endpoint
  getChequePdf: async (chequeId, printOptions = {}) => {
    return await chequeService.printSingleCheque(chequeId, printOptions);
  },

  // Reports functionality
  getChequesByFilters: async (filters) => {
    const queryParams = new URLSearchParams();
    
    if (filters.printDateFrom) queryParams.append('printDateFrom', filters.printDateFrom);
    if (filters.printDateTo) queryParams.append('printDateTo', filters.printDateTo);
    if (filters.chqDateFrom) queryParams.append('chqDateFrom', filters.chqDateFrom);
    if (filters.chqDateTo) queryParams.append('chqDateTo', filters.chqDateTo);
    if (filters.bankName && filters.bankName !== 'all') queryParams.append('bankName', filters.bankName);

    return easyCheckRequest(() => 
      easyCheckApi.get(`/cheques/reports?${queryParams.toString()}`)
    );
  },

  getCrossTabReport: async (filters) => {
    const queryParams = new URLSearchParams();
    
    if (filters.chqDateFrom) queryParams.append('chqDateFrom', filters.chqDateFrom);
    if (filters.chqDateTo) queryParams.append('chqDateTo', filters.chqDateTo);
    if (filters.bankName && filters.bankName !== 'all') queryParams.append('bankName', filters.bankName);

    return easyCheckRequest(() => 
      easyCheckApi.get(`/cheques/reports/crosstab?${queryParams.toString()}`)
    );
  },

  exportReportData: async (data, filename) => {
    // Helper function to export report data as CSV
    const csvContent = convertToCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  },

  // Check duplicate cheque numbers for a specific bank
  checkDuplicateChequeNumbers: async (bankId, chequeNumbers) => {
    try {
      return easyCheckRequest(() => easyCheckApi.post('/cheques/check-duplicates', { BankId: bankId, ChequeNumbers: chequeNumbers }));
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Bulk validate cheque numbers against DB (for history import)
  bulkValidateChequeNumbers: async (chequeNumbers) => {
    try {
      return easyCheckRequest(() => easyCheckApi.post('/cheques/validate-numbers-bulk', { ChequeNumbers: chequeNumbers }));
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Bulk import historical cheques
  importHistoryCheques: async (cheques) => {
    try {
      return easyCheckRequest(() => easyCheckApi.post('/cheques/import-history', { Cheques: cheques }, { timeout: 120000 }));
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Parse free-text paste (tab-separated rows from Excel) — heuristics-only v1
  parsePaste: async ({ rawText, bankAccountId }) => {
    try {
      return easyCheckRequest(() => easyCheckApi.post('/cheques/parse-paste', {
        RawText: rawText,
        BankAccountId: bankAccountId,
      }));
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Extract payment vouchers from an uploaded PDF/image via Claude — one voucher per cheque row.
  // Returns { success, data: { vouchers: [{ beneficiaryName, amount, date, remarks, confidence }] } }.
  // NOTE: bypasses easyCheckRequest/handleApiError so the REAL backend error (Anthropic/exception
  // detail) is surfaced verbatim — handleApiError otherwise masks long 500 messages as a generic
  // "server error", which hides the actual reason a scan failed.
  extractPaymentVouchers: async (file, bankAccountId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bankAccountId', bankAccountId ?? '');
    try {
      const response = await easyCheckApi.post('/cheques/extract-payment-vouchers', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      const d = response.data || {};
      return {
        success: d.Success ?? d.success ?? true,
        data: d.Data ?? d.data ?? d,
        message: d.Message ?? d.message ?? 'Success',
      };
    } catch (error) {
      const d = error?.response?.data;
      const detail = d?.ErrorDetails || d?.errorDetails || d?.Message || d?.message;
      return {
        success: false,
        message: detail ? String(detail).slice(0, 600) : (error?.message || 'Failed to read the voucher.'),
      };
    }
  },

  // Bulk import historical cheques in frontend-driven batches to avoid timeout
  importHistoryChequesBatched: async (cheques, onProgress) => {
    const BATCH_SIZE = 500;
    const totalBatches = Math.ceil(cheques.length / BATCH_SIZE);
    const aggregatedResult = {
      TotalProcessed: 0,
      SuccessCount: 0,
      FailedCount: 0,
      BeneficiariesCreated: 0,
      Results: []
    };

    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      const start = batchIndex * BATCH_SIZE;
      const batchCheques = cheques.slice(start, start + BATCH_SIZE);

      if (onProgress) {
        onProgress({
          currentBatch: batchIndex + 1,
          totalBatches,
          processedSoFar: start,
          totalRecords: cheques.length,
          percent: Math.round((start / cheques.length) * 100)
        });
      }

      try {
        const result = await easyCheckRequest(() =>
          easyCheckApi.post('/cheques/import-history', { Cheques: batchCheques }, { timeout: 180000 })
        );

        if (result.success) {
          const data = result.data;
          aggregatedResult.TotalProcessed += data.TotalProcessed || data.totalProcessed || 0;
          aggregatedResult.SuccessCount += data.SuccessCount || data.successCount || 0;
          aggregatedResult.FailedCount += data.FailedCount || data.failedCount || 0;
          aggregatedResult.BeneficiariesCreated += data.BeneficiariesCreated || data.beneficiariesCreated || 0;

          const batchResults = data.Results || data.results || [];
          // Adjust row numbers to be relative to the full file
          batchResults.forEach(r => {
            r.RowNumber = (r.RowNumber || r.rowNumber || 0) + start;
            r.rowNumber = r.RowNumber;
          });
          aggregatedResult.Results.push(...batchResults);
        } else {
          // Mark all rows in this batch as failed
          for (let i = 0; i < batchCheques.length; i++) {
            aggregatedResult.Results.push({
              RowNumber: start + i + 1,
              rowNumber: start + i + 1,
              ChequeNumber: batchCheques[i].ChequeNumber || '',
              chequeNumber: batchCheques[i].ChequeNumber || '',
              Status: 'FAILED',
              status: 'FAILED',
              Message: result.message || 'Batch failed',
              message: result.message || 'Batch failed'
            });
            aggregatedResult.FailedCount++;
            aggregatedResult.TotalProcessed++;
          }
        }
      } catch (err) {
        // Mark all rows in this batch as failed
        for (let i = 0; i < batchCheques.length; i++) {
          aggregatedResult.Results.push({
            RowNumber: start + i + 1,
            rowNumber: start + i + 1,
            ChequeNumber: batchCheques[i].ChequeNumber || '',
            chequeNumber: batchCheques[i].ChequeNumber || '',
            Status: 'FAILED',
            status: 'FAILED',
            Message: err.message || 'Network error',
            message: err.message || 'Network error'
          });
          aggregatedResult.FailedCount++;
          aggregatedResult.TotalProcessed++;
        }
      }
    }

    if (onProgress) {
      onProgress({
        currentBatch: totalBatches,
        totalBatches,
        processedSoFar: cheques.length,
        totalRecords: cheques.length,
        percent: 100
      });
    }

    return { success: true, data: aggregatedResult };
  }
};

// Helper function to convert JSON data to CSV format
const convertToCSV = (data) => {
  if (!data || data.length === 0) return '';
  
  const headers = Object.keys(data[0]);
  const csvHeaders = headers.join(',');
  
  const csvRows = data.map(row => 
    headers.map(header => {
      const value = row[header];
      // Handle commas and quotes in values
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value || '';
    }).join(',')
  );
  
  return [csvHeaders, ...csvRows].join('\n');
};

// Error handling as per documentation
const handleApiError = (error) => {
  console.error('📛 handleApiError:', error);
  
  if (error.response?.data) {
    const errorData = error.response.data;
    return {
      success: false,
      message: errorData.Message || errorData.message || errorData.ErrorDetails || 'An error occurred',
      errorCode: errorData.ErrorCode || errorData.errorCode,
      validationErrors: errorData.ValidationErrors || errorData.validationErrors || []
    };
  }
  return {
    success: false,
    message: 'Network error. Please try again.',
    errorCode: 'NETWORK_ERROR'
  };
};

export default chequeService; 